import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useResendCodeRegister from "../../../api/auth/register/useSendCodeViaSMS";
import useVerifyCodeRegister, {
  VerifyCodeRegisterRequest,
} from "../../../api/auth/register/useVerifyCodeRegister";
import { useGetTimeToResendCode } from "../../../api/auth/useGetTimeToResendCode";
import SMS from "../../../assets/images/icons/SMS_IC.svg";
import { ConfirmationPopup, Loader } from "../../../components";
import AuthLayout from "../../../Layout/AuthLayout";
import { resetUserDetails } from "../../../redux/reducers/registrationSlice";
import { resetUserState } from "../../../redux/reducers/userSlice";
import { useToast } from "../../../Services/ToastContext";

import Whats from "../../../assets/images/icons/Ic_WhatsApp.svg";
import { useAppSelector, useMaskedPhoneNumber } from "../../../hooks";
import { MessageBtn } from "../../../Share";

interface SendCodeFormInputs {
  code: string[];
}

const VerifyCodeRegister: React.FC = () => {
  const { mutate: resendMutate, isPending: resendIsPending } =
    useResendCodeRegister();

  const {
    mutate: verifyMutate,
    isPending: verifyIsPending,
    error: verifyError,
    isError: verifyIsError,
    reset: verifyReset,
  } = useVerifyCodeRegister();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = useAppSelector((state) => state.registration.userDetails);

  const [resendCodeCount, setResendCodeCount] = useState<number>(0);
  const [resendDate, setResendDate] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const maxResendAttempts = 4;
  const [showResendOptions, setShowResendOptions] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: resendData, 
  } = useGetTimeToResendCode(userDetails.Id);

  useEffect(() => {
    if (resendData && resendData.Status && resendData.Data) {
      setResendCodeCount(resendData.Data.ResendCodeCount);
      setResendDate(parseFloat(resendData.Data.ResendDate));
      const newTimeLeft = calculateTimeLeft(
        resendData.Data.ResendCodeCount,
        parseFloat(resendData.Data.ResendDate),
      );
      setTimeLeft(newTimeLeft);
    }
  }, [resendData]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timeout = setTimeout(() => {
        setTimeLeft((prevTime) => Math.max(prevTime - 1, 0));
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [timeLeft]);

  const calculateTimeLeft = (
    resendCodeCount: number,
    resendDate: number,
  ): number => {
    const now = Date.now() / 1000; // current time in seconds
    const elapsed = now - resendDate;
    let waitTime = 0;

    switch (resendCodeCount) {
      case 1:
        waitTime = 2 * 60; // 2 minutes
        break;
      case 2:
        waitTime = 5 * 60; // 5 minutes
        break;
      case 3:
        waitTime = 30 * 60; // 30 minutes
        break;
      default:
        waitTime = 0;
        break;
    }

    let remainingTime = waitTime - elapsed;

    if (resendCodeCount === 1 && remainingTime > 120) {
      remainingTime = 120;
    }
    if (resendCodeCount === 2 && remainingTime > 300) {
      remainingTime = 300;
    }
    if (resendCodeCount === 3 && remainingTime > 1800) {
      remainingTime = 1800;
    }

    return remainingTime > 0 ? Math.floor(remainingTime) : 0;
  };

  const formatTime = (seconds: number) => {
    const totalSeconds = Math.floor(seconds);
    const m = Math.floor(totalSeconds / 60).toString();
    const s = (totalSeconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const { showToast } = useToast();

  useEffect(() => {
    if (!userDetails.Id) {
      navigate("/login", { replace: true });
    }
  }, [userDetails, navigate]);

  const methods = useForm<SendCodeFormInputs>({
    defaultValues: { code: ["", "", "", "", "", ""] },
  });
  const { handleSubmit, control, setValue, watch, reset } = methods;
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const watchFields = watch("code");
  const isComplete = watchFields.every((code) => code !== "");

  // ---- Added this effect to auto-verify when code is complete ----
  useEffect(() => {
    if (isComplete) {
      handleSubmit(onSubmit)();
    }
  }, [isComplete, handleSubmit]);

  // Remove the user’s ability to submit via Enter key
  // and handle submission ourselves in the effect above.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  // Handle Resend Code
  const handleResendCode = () => {
    if (resendCodeCount >= maxResendAttempts) {
      showToast(t("user_not_able_to_send_more_codes_until_24_hours"), "error");
      return;
    }
    if (timeLeft > 0) {
      return;
    }
    setShowResendOptions(true);
    verifyReset();
  };

  const handleSendCodeViaSMS = () => {
    if (!userDetails.Id) {
      showToast(t("user_id_missing"), "error");
      navigate("/login", { replace: true });
      return;
    }
    resendMutate(
      { Id: userDetails.Id },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: ["GetTimeToResendCode", userDetails.Id],
          });
          resetFields();
          showToast(t("code_resent_successfully"), "success");
          setShowResendOptions(false);
          verifyReset();
        },
        onError: (error: any) => {
          const errorData = error.response?.data;
          showToast(
            errorData?.ErrorMessage ||
              error.message ||
              t("an_error_occurred_please_try_again"),
            "error",
          );
        },
      },
    );
  };

  const handleSendCodeViaWhatsApp = () => {
    if (!userDetails.Id) {
      showToast(t("user_id_missing"), "error");
      navigate("/login", { replace: true });
      return;
    }
    resendMutate(
      { Id: userDetails.Id, Channel: "WhatsApp" },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: ["GetTimeToResendCode", userDetails.Id],
          });
          resetFields();
          showToast(t("code_resent_successfully"), "success");
          setShowResendOptions(false);
          verifyReset();
        },
        onError: (error: any) => {
          const errorData = error.response?.data;
          showToast(
            errorData?.ErrorMessage ||
              error.message ||
              t("an_error_occurred_please_try_again"),
            "error",
          );
        },
      },
    );
  };

  const onSubmit: SubmitHandler<SendCodeFormInputs> = (data) => {
    if (!isComplete) {
      showToast(t("enter_verification_code"), "error");
      return;
    }
    if (!userDetails.Id) {
      showToast(t("user_id_missing"), "error");
      navigate("/login", { replace: true });
      return;
    }

    const requestData: VerifyCodeRegisterRequest = {
      Id: userDetails.Id,
      VerificationCode: data.code.join(""),
    };

    verifyMutate(requestData, {
      onSuccess: () => {
        navigate("/Password-register");
      },
      onError: (error: any) => {
        const errorData = error.response?.data;
        showToast(
          errorData?.ErrorMessage ||
            error.message ||
            t("invalid_code_try_again"),
          "error",
        );
        resetFields();
      },
    });
  };

  const maskedPhoneNumber = useMaskedPhoneNumber(userDetails.PhoneNumber);

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      setValue(`code.${index}`, value);
      if (value.length === 1 && index < 5) {
        inputsRef.current[index + 1]?.focus();
      } else if (value === "" && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
      verifyReset();
    }
  };

  const resetFields = () => {
    for (let i = 0; i < 6; i++) {
      setValue(`code.${i}`, "");
    }
    inputsRef.current[0]?.focus();
  };

  // Prevent page unload
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Prevent going back via the browser’s back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      setIsModalOpen(true);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleConfirmGoBack = () => {
    dispatch(resetUserDetails());
    dispatch(resetUserState());
    setIsModalOpen(false);
    navigate("/sign-up");
  };

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("Text");
    if (/^\d{6}$/.test(pasteData)) {
      for (let i = 0; i < 6; i++) {
        setValue(`code.${i}`, pasteData.charAt(i));
      }
      inputsRef.current[5]?.focus();
    }
  };

  // Reset errors on language change, etc.
  useEffect(() => {
    const handleLanguageChange = () => {
      verifyReset();
      resetFields();
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n, verifyReset]);

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("verify_your_phone_number")}
        description={
          <>
            {t("we_send_code_to")}
            <span dir="ltr"> {maskedPhoneNumber} </span>
          </>
        }
      >
        {/* Use our own handleFormSubmit instead of handleSubmit(onSubmit) */}
        <form className="w-full" onSubmit={handleFormSubmit}>
          <div dir="ltr" className="mb-8 flex justify-between">
            {[...Array(6)].map((_, index) => (
              <Controller
                key={index}
                name={`code.${index}` as const}
                control={control}
                render={({ field }) => (
                  <input
                    className={`h-[50px] w-[75px] rounded-md border border-systemBg text-center text-2xl focus:outline-primary ${
                      verifyIsError
                        ? "border-2 border-solid border-error"
                        : field.value
                          ? "border-primary"
                          : "focus:outline-primary"
                    } ${index === 2 ? "me-8" : "me-2"}`}
                    maxLength={1}
                    {...field}
                    value={field.value || ""}
                    onChange={(e) => handleChange(e.target.value, index)}
                    ref={(el) => (inputsRef.current[index] = el)}
                    aria-label={t("verification_code_digit", {
                      digit: index + 1,
                    })}
                    onPaste={index === 0 ? handlePaste : undefined}
                  />
                )}
              />
            ))}
          </div>
          {verifyIsError && (
            <p className="mb-4 text-center text-error">
              {verifyError?.message || t("invalid_code_try_again")}
            </p>
          )}

          <div className="mb-4 flex items-center justify-center gap-4">
            {resendCodeCount !== null && !showResendOptions && (
              <button
                type="button"
                className={`${
                  timeLeft > 0 || resendCodeCount >= maxResendAttempts
                    ? "text-body-text cursor-not-allowed underline underline-offset-4"
                    : "cursor-pointer font-bold text-primary underline underline-offset-4"
                }`}
                onClick={handleResendCode}
                disabled={
                  timeLeft > 0 ||
                  resendCodeCount >= maxResendAttempts ||
                  resendIsPending
                }
              >
                {t("send_code_again")}
              </button>
            )}
            {timeLeft > 0 && (
              <p className="text-body-text mt-1">{formatTime(timeLeft)}</p>
            )}
          </div>

          {showResendOptions && (
            <div className="my-4 flex flex-col items-center gap-4">
              <MessageBtn
                icon={Whats}
                title={t("send_code_via_whatsapp")}
                onClick={handleSendCodeViaWhatsApp}
                disabled={resendIsPending}
              />
              <MessageBtn
                icon={SMS}
                title={t("send_code_via_sms")}
                onClick={handleSendCodeViaSMS}
                disabled={resendIsPending}
                color="blue"
              />
            </div>
          )}

          {resendCodeCount >= maxResendAttempts && (
            <p className="mb-4 text-center text-error">
              {t("user_not_able_to_send_more_codes_until_24_hours")}
            </p>
          )}

          {(verifyIsPending || resendIsPending) && <Loader />}
        </form>

        {isModalOpen && (
          <ConfirmationPopup
            isModalOpen={isModalOpen}
            title={t("all_info_will_lost")}
            description={t("are_you_sure_to_do_action")}
            onClose={() => setIsModalOpen(false)}
            onConfirm={handleConfirmGoBack}
          />
        )}
      </AuthLayout>
    </FormProvider>
  );
};

export default VerifyCodeRegister;
